import React, { useState } from 'react';
import { 
  BarChart3, Package, Truck, Download, ArrowUpRight, ArrowDownLeft,
  TrendingUp, AlertTriangle, CheckCircle, Clock, Users, MapPin,
  ArrowUp, ArrowDown, Plus, RefreshCw, Eye, Calendar
} from 'lucide-react';

const WarehouseDashboard = () => {
  const [timeRange, setTimeRange] = useState('today');

  const stats = [
    {
      title: 'Items in Stock',
      value: '12,456',
      change: '+2.3%',
      trend: 'up',
      icon: Package,
      color: 'text-blue-600 bg-blue-100'
    },
    {
      title: 'Items Received Today',
      value: '245',
      change: '+15.2%',
      trend: 'up',
      icon: ArrowDownLeft,
      color: 'text-green-600 bg-green-100'
    },
    {
      title: 'Items Shipped Today',
      value: '189',
      change: '+8.1%',
      trend: 'up',
      icon: ArrowUpRight,
      color: 'text-purple-600 bg-purple-100'
    },
    {
      title: 'Low Stock Alerts',
      value: '12',
      change: '-25%',
      trend: 'down',
      icon: AlertTriangle,
      color: 'text-red-600 bg-red-100'
    }
  ];

  const recentActivities = [
    {
      type: 'received',
      message: 'Received 50 units of Premium Cotton T-Shirt',
      time: '10 minutes ago',
      icon: ArrowDownLeft,
      color: 'text-green-600'
    },
    {
      type: 'shipped',
      message: 'Shipped order #ORD-001 (25 items)',
      time: '25 minutes ago',
      icon: ArrowUpRight,
      color: 'text-blue-600'
    },
    {
      type: 'alert',
      message: 'Low stock alert: Cotton Polo Shirt (15 left)',
      time: '1 hour ago',
      icon: AlertTriangle,
      color: 'text-red-600'
    },
    {
      type: 'received',
      message: 'Received 30 units of Casual Cotton Pants',
      time: '2 hours ago',
      icon: ArrowDownLeft,
      color: 'text-green-600'
    },
    {
      type: 'shipped',
      message: 'Shipped order #ORD-002 (40 items)',
      time: '3 hours ago',
      icon: ArrowUpRight,
      color: 'text-blue-600'
    }
  ];

  const pendingTasks = [
    { type: 'Receive Shipment', count: 5, priority: 'high', dueTime: 'Due in 2 hours' },
    { type: 'Quality Check', count: 8, priority: 'medium', dueTime: 'Due today' },
    { type: 'Restock Items', count: 12, priority: 'high', dueTime: 'Overdue' },
    { type: 'Cycle Count', count: 3, priority: 'low', dueTime: 'Due tomorrow' }
  ];

  const warehouseZones = [
    { name: 'Zone A - Textiles', capacity: '85%', items: 3245, status: 'normal' },
    { name: 'Zone B - Accessories', capacity: '92%', items: 1876, status: 'high' },
    { name: 'Zone C - Packaging', capacity: '67%', items: 2134, status: 'normal' },
    { name: 'Zone D - Returns', capacity: '45%', items: 567, status: 'low' }
  ];

  const topMovingItems = [
    { name: 'Premium Cotton T-Shirt', moved: 245, trend: 'up', location: 'Zone A-12' },
    { name: 'Cotton Polo Shirt', moved: 189, trend: 'up', location: 'Zone A-15' },
    { name: 'Casual Cotton Pants', moved: 156, trend: 'down', location: 'Zone A-18' },
    { name: 'Premium Cotton Hoodie', moved: 134, trend: 'up', location: 'Zone A-20' },
    { name: 'Cotton Casual Shirt', moved: 98, trend: 'up', location: 'Zone A-22' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Warehouse Dashboard</h1>
              <p className="text-gray-600">Central Warehouse - Dhaka Operations</p>
            </div>
            <div className="flex items-center space-x-4">
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500"
              >
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
              </select>
              <button className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors flex items-center space-x-2">
                <RefreshCw className="h-4 w-4" />
                <span>Sync Data</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => (
            <div key={index} className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                  <div className={`flex items-center mt-2 text-sm ${
                    stat.trend === 'up' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {stat.trend === 'up' ? (
                      <ArrowUp className="h-4 w-4 mr-1" />
                    ) : (
                      <ArrowDown className="h-4 w-4 mr-1" />
                    )}
                    <span>{stat.change}</span>
                  </div>
                </div>
                <div className={`p-3 rounded-lg ${stat.color}`}>
                  <stat.icon className="h-6 w-6" />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Inventory Movement Chart */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Inventory Movement</h2>
                <div className="flex items-center space-x-2">
                  <button className="text-sm text-gray-600 hover:text-gray-900">Received</button>
                  <button className="text-sm text-gray-600 hover:text-gray-900">Shipped</button>
                  <button className="text-sm text-gray-600 hover:text-gray-900">Stock Levels</button>
                </div>
              </div>
              <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-600">Chart visualization would go here</p>
                  <p className="text-sm text-gray-500">Integration with charting library needed</p>
                </div>
              </div>
            </div>

            {/* Warehouse Zones */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Warehouse Zones</h2>
                <button className="text-sm text-orange-600 hover:text-orange-700 font-medium">View Layout</button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {warehouseZones.map((zone, index) => (
                  <div key={index} className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-gray-900">{zone.name}</h3>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        zone.status === 'high' ? 'bg-red-100 text-red-700' :
                        zone.status === 'normal' ? 'bg-green-100 text-green-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {zone.capacity}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{zone.items.toLocaleString()} items</p>
                    <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          zone.status === 'high' ? 'bg-red-500' :
                          zone.status === 'normal' ? 'bg-green-500' :
                          'bg-yellow-500'
                        }`}
                        style={{ width: zone.capacity }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Moving Items */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Top Moving Items</h2>
                <button className="text-sm text-orange-600 hover:text-orange-700 font-medium">View All</button>
              </div>
              <div className="space-y-4">
                {topMovingItems.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-gray-900">{item.name}</h3>
                      <p className="text-xs text-gray-600">{item.location}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-gray-900">{item.moved} moved</p>
                      <div className={`flex items-center text-xs ${
                        item.trend === 'up' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {item.trend === 'up' ? (
                          <TrendingUp className="h-3 w-3 mr-1" />
                        ) : (
                          <ArrowDown className="h-3 w-3 mr-1" />
                        )}
                        <span>{item.trend === 'up' ? '+' : '-'}5%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Pending Tasks */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Pending Tasks</h2>
              <div className="space-y-4">
                {pendingTasks.map((task, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{task.type}</p>
                      <p className="text-sm text-gray-600">{task.count} items</p>
                      <p className="text-xs text-gray-500">{task.dueTime}</p>
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                      task.priority === 'high' ? 'bg-red-100 text-red-700' :
                      task.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {task.priority}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Recent Activity</h2>
              <div className="space-y-4">
                {recentActivities.map((activity, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className={`p-2 rounded-lg ${activity.color.replace('text-', 'bg-').replace('-600', '-100')}`}>
                      <activity.icon className={`h-4 w-4 ${activity.color}`} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-900">{activity.message}</p>
                      <p className="text-xs text-gray-500">{activity.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-orange-50 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
              <div className="space-y-3">
                <button className="w-full bg-orange-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors flex items-center justify-center space-x-2">
                  <ArrowDownLeft className="h-4 w-4" />
                  <span>Receive Shipment</span>
                </button>
                <button className="w-full border border-orange-600 text-orange-600 py-2 px-4 rounded-lg text-sm font-medium hover:bg-orange-50 transition-colors flex items-center justify-center space-x-2">
                  <ArrowUpRight className="h-4 w-4" />
                  <span>Ship Order</span>
                </button>
                <button className="w-full border border-gray-300 text-gray-700 py-2 px-4 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2">
                  <Eye className="h-4 w-4" />
                  <span>Cycle Count</span>
                </button>
              </div>
            </div>

            {/* Warehouse Info */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Warehouse Info</h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Total Capacity</span>
                  <span className="text-sm font-medium text-gray-900">15,000 items</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Current Utilization</span>
                  <span className="text-sm font-medium text-gray-900">83%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Available Space</span>
                  <span className="text-sm font-medium text-green-600">2,544 items</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Active Staff</span>
                  <span className="text-sm font-medium text-gray-900">24 workers</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WarehouseDashboard;